/*
 * Walkie Talkie multi-piece add to cart: the set builder (sections/wt-set-builder.liquid) and the
 * "Complete the gift" picks under Add to cart (snippets/wt-bundle-prompt.liquid).
 *
 * <wt-piece> wraps one product: a JSON list of its variants plus one <select data-option="N"> per
 * option. It works out the chosen variant and its price, and fires "wt:piece" on every change.
 */
(() => {
  if (customElements.get('wt-piece')) return; // already loaded on this page
  const wtMoney = (cents) => '$' + (cents / 100).toFixed(2);

  class WtPiece extends HTMLElement {
    connectedCallback() {
      this.variants = JSON.parse(this.querySelector('script[type="application/json"]').textContent);
      this.selects = [...this.querySelectorAll('select[data-option]')];
      this.qtyInput = this.querySelector('[data-qty]');
      this.selects.forEach((s) => s.addEventListener('change', () => this.update()));
      this.qtyInput?.addEventListener('change', () => this.update());
      this.update();
    }

    get variant() {
      const chosen = this.selects.map((s) => s.value);
      if (chosen.some((v) => !v)) return null;
      return this.variants.find((v) => v.options.every((o, i) => o === chosen[i])) || null;
    }

    /** Photo for the options picked so far (Color is enough; Size doesn't change the photo). */
    get image() {
      const chosen = this.selects.map((s) => s.value);
      const v = this.variants.find((x) => x.img && x.options.every((o, i) => !chosen[i] || o === chosen[i]));
      return v ? v.img : 0;
    }

    get qty() {
      return Math.max(0, parseInt(this.qtyInput?.value || '1', 10) || 0);
    }

    update() {
      const v = this.variant;
      const price = this.querySelector('[data-price]');
      const note = this.querySelector('[data-note]');
      if (price) price.textContent = wtMoney(v ? v.price : this.variants[0].price);
      if (note) note.textContent = this.selects.some((s) => !s.value) ? '' : v && v.available ? '' : 'Sold out in that size';
      this.dataset.ready = v && v.available && this.qty > 0 ? 'true' : 'false';
      this.dispatchEvent(new CustomEvent('wt:piece', { bubbles: true }));
    }
  }
  customElements.define('wt-piece', WtPiece);

  /** Add several lines at once, then open the cart drawer (or go to /cart if the theme has none). */
  async function wtAddToCart(items) {
    const drawer = document.querySelector('cart-drawer');
    const body = { items };
    if (drawer) {
      body.sections = drawer.getSectionsToRender().map((s) => s.id);
      body.sections_url = window.location.pathname;
    }
    const res = await fetch(window.Shopify?.routes?.root ? `${window.Shopify.routes.root}cart/add.js` : '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.description || json.message || 'Could not add to cart');
    if (!drawer) {
      window.location.href = '/cart';
      return;
    }
    drawer.classList.remove('is-empty');
    drawer.renderContents({ id: items[0].id, sections: json.sections });
  }

  /* Set builder: one size per piece, a running total, one button for everything picked. */
  class WtSetBuilder extends HTMLElement {
    connectedCallback() {
      this.pieces = [...this.querySelectorAll('wt-piece')];
      this.button = this.querySelector('[data-add]');
      this.addEventListener('wt:piece', (e) => {
        this.render();
        this.showPieceImage(e.target);
      });
      this.button.addEventListener('click', () => this.add());
      this.setupGallery();
      this.render();
    }

    /* One slider with every piece's photos: arrows, thumbnails, swipe (scroll snap). */
    setupGallery() {
      this.track = this.querySelector('[data-track]');
      if (!this.track) return;
      this.slides = [...this.track.children];
      this.thumbs = [...this.querySelectorAll('[data-thumbs] button')];
      this.prev = this.querySelector('[data-prev]');
      this.next = this.querySelector('[data-next]');
      this.prev.addEventListener('click', () => this.goTo(this.current - 1));
      this.next.addEventListener('click', () => this.goTo(this.current + 1));
      this.thumbs.forEach((t) => t.addEventListener('click', () => this.goTo(parseInt(t.dataset.index, 10))));
      this.track.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') this.goTo(this.current - 1);
        if (e.key === 'ArrowRight') this.goTo(this.current + 1);
      });
      let t;
      this.track.addEventListener('scroll', () => {
        clearTimeout(t);
        t = setTimeout(() => this.markCurrent(), 60);
      });
      // A piece's little photo jumps the slider to that piece instead of leaving the page.
      this.pieces.forEach((piece) => {
        piece._shownImg = piece.image;
        piece.querySelector('.wt-piece__img')?.addEventListener('click', (e) => {
          const i = this.slides.findIndex((s) => s.dataset.piece === piece.dataset.product);
          if (i < 0) return;
          e.preventDefault();
          this.goTo(i);
          this.track.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        });
      });
      this.markCurrent();
    }

    get current() {
      return Math.round(this.track.scrollLeft / this.track.clientWidth);
    }

    goTo(i) {
      if (!this.slides || i < 0 || i >= this.slides.length) return;
      this.track.scrollTo({ left: i * this.track.clientWidth });
    }

    markCurrent() {
      const i = this.current;
      this.thumbs.forEach((t, n) => t.setAttribute('aria-current', n === i ? 'true' : 'false'));
      const thumb = this.thumbs[i];
      // Keep the active thumbnail in view without scrolling the page itself.
      if (thumb) thumb.parentElement.scrollTo({ left: thumb.offsetLeft - thumb.parentElement.clientWidth / 2 + thumb.clientWidth / 2, behavior: 'smooth' });
      this.prev.disabled = i <= 0;
      this.next.disabled = i >= this.slides.length - 1;
    }

    /** When a piece's color changes, show that color's photo. */
    showPieceImage(piece) {
      if (!this.slides) return;
      const img = piece.image;
      if (!img || img === piece._shownImg) return;
      piece._shownImg = img;
      const i = this.slides.findIndex((s) => s.dataset.img === String(img));
      if (i >= 0) this.goTo(i);
    }

    picked() {
      return this.pieces.filter((p) => p.dataset.ready === 'true');
    }

    render() {
      const picked = this.picked();
      const count = picked.reduce((n, p) => n + p.qty, 0);
      const subtotal = picked.reduce((n, p) => n + p.variant.price * p.qty, 0);
      const min = parseInt(this.dataset.dealMin, 10);
      const pct = parseInt(this.dataset.dealPct, 10);
      const off = count >= min ? Math.round((subtotal * pct) / 100) : 0;
      this.querySelector('[data-subtotal]').textContent = wtMoney(subtotal);
      this.querySelector('[data-total]').textContent = wtMoney(subtotal - off);
      const saveRow = this.querySelector('[data-save-row]');
      saveRow.hidden = off === 0;
      this.querySelector('[data-save]').textContent = '−' + wtMoney(off);
      const hint = this.querySelector('[data-hint]');
      if (count === 0) hint.textContent = 'Pick a size for each piece you want.';
      else if (count < min) hint.textContent = `Add ${min - count} more to save ${pct}% on everything.`;
      else hint.textContent = `Buy ${min}, save ${pct}% unlocked. It comes off at checkout.`;
      this.button.disabled = count === 0;
      this.button.querySelector('span').textContent = count === 0 ? 'Pick your sizes' : `Add ${count} ${count === 1 ? 'piece' : 'pieces'} to cart · ${wtMoney(subtotal - off)}`;
    }

    async add() {
      const items = this.picked().map((p) => ({ id: p.variant.id, quantity: p.qty }));
      if (!items.length) return;
      const err = this.querySelector('[data-error]');
      err.textContent = '';
      this.button.setAttribute('aria-busy', 'true');
      this.button.disabled = true;
      try {
        await wtAddToCart(items);
      } catch (e) {
        err.textContent = e.message;
      } finally {
        this.button.removeAttribute('aria-busy');
        this.render();
      }
    }
  }
  customElements.define('wt-set-builder', WtSetBuilder);

  /* "Complete the gift": each suggestion has its own Add button. */
  class WtBundlePrompt extends HTMLElement {
    connectedCallback() {
      this.addEventListener('wt:piece', (e) => {
        const btn = e.target.querySelector('[data-add]');
        if (btn) btn.disabled = e.target.dataset.ready !== 'true';
      });
      this.querySelectorAll('wt-piece').forEach((piece) => {
        const btn = piece.querySelector('[data-add]');
        btn.disabled = piece.dataset.ready !== 'true';
        btn.addEventListener('click', async () => {
          const v = piece.variant;
          if (!v) return;
          btn.setAttribute('aria-busy', 'true');
          btn.disabled = true;
          try {
            await wtAddToCart([{ id: v.id, quantity: 1 }]);
            btn.textContent = 'Added';
          } catch (e) {
            piece.querySelector('[data-note]').textContent = e.message;
            btn.disabled = false;
          } finally {
            btn.removeAttribute('aria-busy');
          }
        });
      });
    }
  }
  customElements.define('wt-bundle-prompt', WtBundlePrompt);
})();
